import { isArray, isEmpty, startsWith, trimStart } from 'lodash';
import { FieldPath } from 'react-hook-form';

import { EmployeeCommutingLocationDetail, RouteTripType } from '@/apis/gin/boundary-database/employee-commuting.dto';
import { RMTMeans, RMTMode, RMTRouteType } from '@/app/factory-database/src/enums/rmt';
import { isAerosolSprayWithHfcs, isGhgOnlyHasNA } from '@/app/org-cfv/[projectId]/src/utils';
import { EmissionSourceEquipments } from '@/app/org-cfv/src/constants/EquipmentConstants';
import { EmissionSourceProcesses } from '@/app/org-cfv/src/constants/ProcessConstants';
import { PurchasedElectricityType } from '@/app/org-cfv/src/constants/PurchasedElectricityConstants';
import {
  ActivityUnit,
  CatOfActivityRelatedToFuelEnergy,
  HypotheticalSituationType,
} from '@/app/org-cfv/src/enums/ActivityDataTypeEnum';
import {
  EmissionFactorByFactorErrorType,
  EmissionSourceCategory,
  EmissionSourceFactorSource,
  EmissionSourceFactorType,
  EmissionSourceFactorUnitType,
  EmissionSourceGreenHouseGases,
  EmissionSourceMeasurementMethod,
  EmissionSourceRawMaterialOrProduct,
  EmissionSourceSubCategory,
  RawMaterialOrProduct4_1b,
} from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import {
  DischargeLocation,
  FugitiveCategory,
  FugitiveEquipmentType,
  FugitiveType,
  TreatmentMethod,
} from '@/app/org-cfv/src/enums/FugitiveEmissionEnum';
import { RawMaterialProducts } from '@/app/org-cfv/src/enums/RawMaterialProductEnum';
import { ServiceEmissionType } from '@/app/org-cfv/src/enums/ServiceEnum';
import {
  EMISSION_TYPE_ONE_OTHER_OPTIONS,
  EMISSION_TYPE_TWO_OTHER_OPTIONS,
  EmissionTypeOneOptions,
  EmissionTypeTwoOptions,
  TreatmentAndDisposalMethodOptions,
  WasteOperationEmissionType,
  WasteTypeForMaterialsRecoveryOptions,
} from '@/app/org-cfv/src/enums/WasteOperationEnum';
import { isGhgFactorValid } from '@/app/org-cfv/utils';
import { NextTFunction } from '@/i18n/client';
import { ScenarioStatus } from '@/modules/ActivityData/domain/ActivityData';
import { DocumentType } from '@/typings/common';
import {
  FugitiveCategoryType,
  getFugitiveCategoryType,
  getTreatmentMethodType,
  getWastewaterType,
  TreatmentMethodType,
  WASTE_WATER_TYPES,
  WastewaterType,
} from '@/utils/emission';
import {
  isNumber,
  isPositiveNumberOrZero,
  numberRequiredSchema,
  numberSchema,
  stringRequiredSchema,
  stringSchema,
} from '@/utils/validations';
import yup from '@/utils/yup';

import { getRefrigerantSettingSchema } from '../components/EmissionFactorsForm/RefrigerantSettingModal';
import {
  CommercialSewageRegulatedFactor,
  CommercialSewageUnregulatedFactor,
  IndustrialSewageFactor,
} from '../components/EmissionFactorsForm/typings';
import { getDefaultFactorSourceType, getFactor } from '../components/util';

type KeysIndustrialSewageFactor = Array<keyof IndustrialSewageFactor>;
type KeysCommercialSewageRegulatedFactor = Array<keyof CommercialSewageRegulatedFactor>;
type KeysCommercialSewageUnregulatedFactor = Array<keyof CommercialSewageUnregulatedFactor>;

export const PROCESS_NUMBER_BEGIN = 'M';
export const EQUIPMENT_NUMBER_BEGIN = 'E';
export const PROCESS_NUMBER_DIGITS = 2;
export const EQUIPMENT_NUMBER_DIGITS = 3;
export const PROCESS_NUMBER_REX = new RegExp(`^\\d{${PROCESS_NUMBER_DIGITS}}$`);
export const EQUIPMENT_NUMBER_REX = new RegExp(`^\\d{${EQUIPMENT_NUMBER_DIGITS}}$`);

const checkIsStationarySourcesLicenseValid = (input: CreateEmissionType): boolean => {
  const { isStationarySourcesLicense, processNumber = '', equipmentNumber = '' } = input.metadata;
  const processNumberTrimBegin = startsWith(processNumber, PROCESS_NUMBER_BEGIN)
    ? trimStart(processNumber, PROCESS_NUMBER_BEGIN)
    : processNumber;
  const equipmentCodeTrimBegin = startsWith(equipmentNumber, EQUIPMENT_NUMBER_BEGIN)
    ? trimStart(equipmentNumber, EQUIPMENT_NUMBER_BEGIN)
    : equipmentNumber;
  const checkProcessNumber = PROCESS_NUMBER_REX.test(processNumberTrimBegin);
  const checkEquipmentNumber = EQUIPMENT_NUMBER_REX.test(equipmentCodeTrimBegin);

  return isStationarySourcesLicense ? checkProcessNumber && checkEquipmentNumber : true;
};

export const isStepOneComplete = (input: CreateEmissionType): boolean => {
  // Only categories/subcategories that requires emission forms need to include validation for completeness
  if (input.subCategory === EmissionSourceSubCategory.CAT_1_4) {
    const metadata = input.metadata;
    if (metadata.equipment && !EmissionSourceEquipments.includes(metadata.equipment)) {
      return false;
    }
    if (metadata.process && !EmissionSourceProcesses.includes(metadata.process)) {
      return false;
    }
    const isFormValid =
      Boolean(input.name && metadata && metadata.measurementMethod) && checkIsStationarySourcesLicenseValid(input);

    if (!isFormValid) {
      return false;
    }
    if (!metadata?.fugitiveCategory || !metadata?.fugitiveType) {
      return false;
    }
    const fugitiveCategoryType = getFugitiveCategoryType(metadata.fugitiveCategory);
    const wastewaterType = getWastewaterType(metadata.fugitiveType);
    const treatmentMethodType = getTreatmentMethodType(metadata?.customWastewater?.treatmentMethod);

    // check others fields
    if (fugitiveCategoryType === FugitiveCategoryType.OTHER && !metadata.fugitiveCategoryOtherName) {
      return false;
    }
    if (metadata.fugitiveType === FugitiveType.FT_OTHER && !metadata.fugitiveTypeOtherName) {
      return false;
    }

    // skip wastewater, due to metadata.equipmentType is not for wastewater
    if (fugitiveCategoryType !== FugitiveCategoryType.WASTEWATER) {
      if (!metadata.equipmentType) {
        return false;
      }
      if (metadata.equipmentType === FugitiveEquipmentType.ET_OTHER && !metadata.equipmentTypeOtherName) {
        return false;
      }
    } else {
      // wastewater
      if (wastewaterType === WastewaterType.COMMERCIAL) {
        if (!metadata?.customWastewater?.treatmentMethod) {
          return false;
        }
        if (treatmentMethodType === TreatmentMethodType.UNCOLLECTED && !metadata?.customWastewater?.dischargeLocation) {
          return false;
        }
      } else if (wastewaterType === WastewaterType.INDUSTRIAL) {
        if (!metadata?.customWastewater?.dischargePathwayOrSystemType) {
          return false;
        }
      }
    }

    return isFormValid && isReportRawMaterial2Valid(input) && isProcessValid(input) && isEquipmentValid(input);
  }

  if (input.subCategory === EmissionSourceSubCategory.CAT_2_2) {
    const metadata = input.metadata;

    if (metadata.equipment && !EmissionSourceEquipments.includes(metadata.equipment)) {
      return false;
    }
    if (metadata.process && !EmissionSourceProcesses.includes(metadata.process)) {
      return false;
    }

    return (
      Boolean(input.name && metadata && metadata.measurementMethod) &&
      checkIsStationarySourcesLicenseValid(input) &&
      isReportRawMaterial2Valid(input) &&
      isProcessValid(input) &&
      isEquipmentValid(input)
    );
  }

  if (input.subCategory === EmissionSourceSubCategory.CAT_4_1B) {
    return (
      checkIsStationarySourcesLicenseValid(input) &&
      isFourPointTwoStepOneComplete(input) &&
      isReportRawMaterial2Valid(input) &&
      isProcessValid(input) &&
      isEquipmentValid(input)
    );
  }

  if (input.subCategory === EmissionSourceSubCategory.CAT_4_3) {
    const metadata = input.metadata;
    if (metadata.emissionType === WasteOperationEmissionType.OTHER) {
      if (!metadata.emissionTypeOther) {
        return false;
      }
    }
    if (metadata.emissionType1 && EMISSION_TYPE_ONE_OTHER_OPTIONS.includes(metadata.emissionType1)) {
      if (!metadata.emissionTypeOther1) {
        return false;
      }
    }
    if (metadata.emissionType2 && EMISSION_TYPE_TWO_OTHER_OPTIONS.includes(metadata.emissionType2)) {
      if (!metadata.emissionTypeOther2) {
        return false;
      }
    }
    if (
      metadata.emissionType1 &&
      ![
        ...Object.keys(TreatmentAndDisposalMethodOptions),
        ...Object.keys(WasteTypeForMaterialsRecoveryOptions),
      ].includes(metadata.emissionType1)
    ) {
      if (!metadata.emissionType2) {
        return false;
      }
    }
    const isFormValid =
      Boolean(
        input.name &&
          metadata &&
          metadata.emissionType &&
          (metadata.emissionType === WasteOperationEmissionType.OTHER || metadata.emissionType1) &&
          metadata.measurementMethod,
      ) &&
      checkIsStationarySourcesLicenseValid(input) &&
      isReportRawMaterial2Valid(input) &&
      isProcessValid(input) &&
      isEquipmentValid(input);

    if (!isFormValid) {
      return false;
    }
    return isFormValid;
  }

  if (input.subCategory === EmissionSourceSubCategory.CAT_4_5) {
    const metadata = input.metadata;
    if (metadata.emissionType === ServiceEmissionType.USE_OF_SERVICES_OTHER) {
      if (!metadata.emissionTypeOther) {
        return false;
      }
    }
    return Boolean(input.name && metadata && metadata.emissionType && metadata.measurementMethod);
  }

  return false;
};

const isReportRawMaterial2Valid = (input: CreateEmissionType): boolean => {
  if (!input.metadata.reportRawMaterial2) {
    return true;
  }

  const options = Object.values(RawMaterialProducts);
  if (options.includes(input.metadata.reportRawMaterial2)) {
    return true;
  }

  return false;
};

const isProcessValid = (input: CreateEmissionType): boolean => {
  if (!input.metadata.process) {
    return true;
  }

  const options = Object.values(EmissionSourceProcesses);
  if (options.includes(input.metadata.process)) {
    return true;
  }

  return false;
};

const isEquipmentValid = (input: CreateEmissionType): boolean => {
  if (!input.metadata.equipment) {
    return true;
  }

  const options = Object.values(EmissionSourceEquipments);
  if (options.includes(input.metadata.equipment)) {
    return true;
  }

  return false;
};

const isFourPointTwoStepOneComplete = (input: CreateEmissionType): boolean => {
  return !(
    isEmpty(input.name) ||
    isEmpty(input.metadata.relatedFuelEnergyCategory) ||
    isEmpty(input.metadata.rawMaterial4_1b)
  );
};

type FactorGhgKey = 'ch4' | 'co2' | 'n2o' | 'hfcs' | 'pfcs' | 'sf6' | 'nf3';
type GHGValues = {
  [K in EmissionSourceGreenHouseGases]: {
    factorUnit?: ActivityUnit | undefined;
    factorUnitType?: EmissionSourceFactorUnitType | undefined;
    gasKey?: EmissionSourceGreenHouseGases | undefined;
    gasName?: string | undefined;
    ghgUnit?: ActivityUnit | undefined;
    factor: number;
  };
};

export const isStepTwoComplete = (input: CreateEmissionType, t: NextTFunction): boolean => {
  const factor =
    getFactor(input, getDefaultFactorSourceType(input.subCategory)) ||
    ({} as yup.InferType<typeof CreateEmissionFactorSchema>);

  const { type, name, factorSourceKey, factorSourceName, activationDate, factorErrorType, gasData } = factor;

  const purchasedOrAcquiredElectricityCategory = input.metadata.purchasedOrAcquiredElectricityCategory;
  const greenHouseEmissionType = input.metadata.greenHouseEmissionType;
  let isValidGhgValues = true;
  let isValidGhgUnit = true;
  const ghgValue: GHGValues | undefined = gasData?.reduce(
    (all, gas) => ({ ...all, [`${gas.gasKey}`]: gas }),
    {} as GHGValues,
  );

  if (
    type === EmissionSourceFactorType.CUSTOMIZED &&
    purchasedOrAcquiredElectricityCategory !== PurchasedElectricityType.POW_3 &&
    !isGhgOnlyHasNA(input.metadata.greenHouseEmissionType)
  ) {
    isValidGhgValues = greenHouseEmissionType.every((ghg) => isGhgFactorValid(ghgValue && ghgValue?.[ghg]?.factor));
    isValidGhgUnit = greenHouseEmissionType.every(
      (ghg) => ghgValue?.[ghg]?.factorUnit && ghgValue?.[ghg]?.ghgUnit && ghgValue?.[ghg]?.factorUnitType,
    );
  }

  return Boolean(
    factor &&
      type &&
      factorErrorType &&
      name &&
      activationDate &&
      (type === EmissionSourceFactorType.CUSTOMIZED || factorSourceKey) &&
      factorSourceName &&
      isValidGhgValues &&
      isValidGhgUnit &&
      greenHouseEmissionType.length > 0 &&
      isOnePointFourStepTwoValid(input, t) &&
      isFourPointTwoStepTwoComplete(input) &&
      isFourPointThreeStepTwoValid(input, factor.activationDate),
  );
};

const isFourPointThreeStepTwoValid = (input: CreateEmissionType, activationDate: Date | undefined) => {
  if (input.subCategory !== EmissionSourceSubCategory.CAT_4_3) {
    return true;
  }
  const isWasteCodeValid = input.metadata.wasteManagementCode && input.metadata.wasteSiteCode;
  return isWasteCodeValid && activationDate && isCat4_3TransportationFactorValid(input);
};

const isOnePointFourStepTwoValid = (input: CreateEmissionType, t: NextTFunction) => {
  if (input.subCategory !== EmissionSourceSubCategory.CAT_1_4) {
    return true;
  }

  let isValid = true;
  const type = input.factors?.[0]?.type;
  const fugitiveCategory = input.metadata.fugitiveCategory;
  const fugitiveCategoryType = getFugitiveCategoryType(fugitiveCategory);
  switch (fugitiveCategoryType) {
    case FugitiveCategoryType.AEROSOL_SPRAY:
      isValid =
        type === EmissionSourceFactorType.CUSTOMIZED &&
        isAerosolSprayWithHfcs(input.metadata.greenHouseEmissionType, fugitiveCategory)
          ? isRefrigerantCompositionsValid(input, t)
          : true;
      break;
    case FugitiveCategoryType.REFRIGERANT:
      if (
        input.metadata.fugitiveCalculationMethod !== HypotheticalSituationType.ACTUAL_FILL_CAL &&
        input.metadata.fugitiveCalculationMethod !== HypotheticalSituationType.AVG_FUGITIVE_METH
      ) {
        isValid = false;
        break;
      }
      if (input.metadata.fugitiveCalculationMethod === HypotheticalSituationType.AVG_FUGITIVE_METH) {
        const emissionFactor = input.metadata.fugitiveRateCalculation?.emissionFactor;
        const recoveryEfficiency = input.metadata.fugitiveRateCalculation?.recoveryEfficiency;
        const preventionEquipmentUsageRate = input.metadata.fugitiveRateCalculation?.preventionEquipmentUsageRate;

        isValid =
          isPositiveNumberOrZero(emissionFactor) &&
          isPositiveNumberOrZero(recoveryEfficiency) &&
          isPositiveNumberOrZero(preventionEquipmentUsageRate);
      }
      if (isValid) {
        isValid = isRefrigerantCompositionsValid(input, t);
      }
      break;
    case FugitiveCategoryType.SEPTIC_TANK:
      if (type === EmissionSourceFactorType.CUSTOMIZED) {
        const bodFactor = input.metadata.sewagePool.bodFactor;
        const averageWastewaterConcentration = input.metadata.sewagePool.averageWastewaterConcentration;
        const perCapitaHourlyWastewaterVolume = input.metadata.sewagePool.perCapitaHourlyWastewaterVolume;
        const treatmentEfficiency = input.metadata.sewagePool.treatmentEfficiency;

        isValid =
          isNumber(bodFactor) &&
          isNumber(averageWastewaterConcentration) &&
          isNumber(perCapitaHourlyWastewaterVolume) &&
          isNumber(treatmentEfficiency);
      }
      break;
    case FugitiveCategoryType.WASTEWATER:
      if (type === EmissionSourceFactorType.CUSTOMIZED) {
        isValid = isWastewaterValid(input);
      }
      break;
  }

  return isValid;
};

const isFourPointTwoStepTwoComplete = (input: CreateEmissionType): boolean => {
  if (input.subCategory !== EmissionSourceSubCategory.CAT_4_1B) {
    return true;
  }
  const relatedFuelEnergyCategory = input.metadata.relatedFuelEnergyCategory;

  if (relatedFuelEnergyCategory === CatOfActivityRelatedToFuelEnergy.CARFE_4) {
    const lossFactor = getFactor(input, EmissionSourceFactorSource.TDLOSS);

    if (!lossFactor || !isFactorValid(lossFactor, ['co2'])) {
      return false;
    }
  }

  return true;
};

const isCat4_3TransportationFactorValid = (input: CreateEmissionType): boolean => {
  const factor = getFactor(input, EmissionSourceFactorSource.TRANSPORTATION);
  return factor ? isFactorValid(factor) : false;
};

const isFactorValid = (
  factor: Exclude<CreateEmissionType['factors'], undefined>[number],
  neededGhg?: FactorGhgKey[],
) => {
  let isValid = !isEmpty(factor.type) && !isEmpty(factor.name) && !isEmpty(factor.factorSourceType);

  if (factor.type === EmissionSourceFactorType.DEFAULT && isEmpty(factor.factorSourceKey)) {
    return false;
  }

  if (factor.factorSourceType === EmissionSourceFactorSource.TDLOSS) {
    if (isEmpty(factor.factorSourceName) || !factor.activationDate) {
      return false;
    }
  } else {
    if (isEmpty(factor.factorErrorType)) {
      return false;
    }
  }

  factor.gasData?.forEach((gasData) => {
    if (isNumber(gasData.factor)) {
      if (isEmpty(gasData.ghgUnit) || isEmpty(gasData.factorUnit) || isEmpty(gasData.factorUnitType)) {
        isValid = false;
      }
    } else if (gasData.gasKey && neededGhg?.includes(gasData?.gasKey?.toLowerCase() as FactorGhgKey)) {
      isValid = false;
    }
  });

  return isValid;
};

const isRefrigerantCompositionsValid = (input: CreateEmissionType, t: NextTFunction): boolean => {
  const refrigerantCompositions = input.metadata.refrigerantComposition || [];
  const refrigerantSettingSchema = getRefrigerantSettingSchema(t);
  if (refrigerantCompositions.length <= 0) {
    return false;
  }
  try {
    for (const r of refrigerantCompositions) {
      refrigerantSettingSchema.validateSync(r, { strict: true });
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

const isWastewaterValid = (input: CreateEmissionType): boolean => {
  const fugitiveType = input.metadata?.fugitiveType;
  const wastewaterType = getWastewaterType(fugitiveType);
  if (!WASTE_WATER_TYPES.includes(wastewaterType as WastewaterType)) {
    return true;
  }
  if (isEmpty(input.metadata?.customWastewater)) {
    return false;
  }
  const isIndustrial = wastewaterType === WastewaterType.INDUSTRIAL;
  let keys: Array<
    keyof IndustrialSewageFactor | keyof CommercialSewageRegulatedFactor | keyof CommercialSewageUnregulatedFactor
  > = [];
  if (isIndustrial) {
    const _keys: KeysIndustrialSewageFactor = ['maxMethaneProduction', 'methaneCorrectionFactor', 'n2oEmissionFactor'];
    keys = [..._keys];
  } else {
    const treatmentMethodType = getTreatmentMethodType(input.metadata.customWastewater?.treatmentMethod);
    const isUnRegulated = treatmentMethodType === TreatmentMethodType.UNCOLLECTED;
    if (isUnRegulated) {
      const _keys: KeysCommercialSewageUnregulatedFactor = [
        'wastewaterTreatmentRate',
        'maxMethaneProduction',
        'methaneCorrectionFactor',
        'organicMatterProductionPPPD',
        'ch4IndustrialWastewaterDischargeFactor',
        'proteinSupplyPPPD',
        'nitrogenToProteinRatio',
        'nonProteinSaturatedWastewaterFactor',
        'n2oIndustrialWastewaterDischargeFactor',
        'n2oEmissionFactor',
      ];
      keys = [..._keys];
    } else {
      const _keys: KeysCommercialSewageRegulatedFactor = [
        'proteinSupplyPPPD',
        'nitrogenToProteinRatio',
        'nonProteinSaturatedWastewaterFactor',
        'n2oIndustrialWastewaterDischargeFactor',
        'n2oEmissionFactor',
        'ch4EmissionFactor',
      ];
      keys = [..._keys];
    }
  }

  return (
    isArray(keys) &&
    keys.every((key) => {
      const value = input.metadata?.customWastewater?.[key];
      return isNumber(value);
    })
  );
};

export const isFormComplete = (input: CreateEmissionType, t: NextTFunction): boolean => {
  return isStepOneComplete(input) && isStepTwoComplete(input, t);
};

// make each ghg factor、factorUnit、factorUnitType optional, due to not every ghg is emitted for each emission
const CreateEmissionFactorSchema = yup.object({
  type: yup.mixed<EmissionSourceFactorType>().oneOf(Object.values(EmissionSourceFactorType)).defined(),
  factorSourceType: yup.mixed<EmissionSourceFactorSource>().oneOf(Object.values(EmissionSourceFactorSource)).defined(),
  factorErrorType: yup
    .mixed<EmissionFactorByFactorErrorType>()
    .oneOf(Object.values(EmissionFactorByFactorErrorType))
    .defined()
    .nullable(),
  name: yup.string().required(),
  factorSourceName: yup.string(),
  factorSourceKey: yup.string(),
  activationDate: yup.date(),
  greenHouseEmissionType: yup
    .array(yup.mixed<EmissionSourceGreenHouseGases>().oneOf(Object.values(EmissionSourceGreenHouseGases)).defined())
    .required(),
  gasData: yup.array(
    yup.object({
      factor: yup.number().required().default(0),
      factorUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
      factorUnitType: yup.mixed<EmissionSourceFactorUnitType>().oneOf(Object.values(EmissionSourceFactorUnitType)),
      gasKey: yup.string(), // e.g. "CO2"
      gasName: yup.string(), // e.g. "CO₂"
      ghgUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
      isRefrigerant: yup.boolean().default(false),
      isPFCS: yup.boolean().default(false),
      isCO2Equivalent: yup.boolean().default(false),
    }),
  ),
});

const CreateEmissionMetadataSchema = yup.object({
  reportRawMaterial1: yup
    .mixed<EmissionSourceRawMaterialOrProduct>()
    .oneOf(Object.values(EmissionSourceRawMaterialOrProduct))
    .required()
    .nullable(),
  reportRawMaterial2: yup
    .mixed<RawMaterialProducts>()
    .oneOf(Object.values(RawMaterialProducts), '不存在')
    .required('required')
    .nullable(),
  purchasedEnergyType: yup.string().required().nullable(),
  process: yup.string().required(),
  equipment: yup.string().required(),
  greenHouseEmissionType: yup
    .array(yup.mixed<EmissionSourceGreenHouseGases>().oneOf(Object.values(EmissionSourceGreenHouseGases)).defined())
    .required(),
  isStationarySourcesLicense: yup.boolean().required(),
  processNumber: yup.string(),
  equipmentNumber: yup.string(),
  isCoExistingDevice: yup.boolean().required(),
  isBioEnergySource: yup.boolean().required(),
  measurementMethod: yup.mixed().oneOf(Object.values(EmissionSourceMeasurementMethod)),
  activityDocumentDescription: yup.string().nullable(),
  relatedId: yup.string().nullable(),
});

const CreateEmissionUnitConversionSchema = yup.object({
  unitAfterConversion: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
  unitBeforeConversion: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
  enabled: yup.boolean(),
  factor: yup.number(),
});

const CreateEmissionBaseSchema = yup.object({
  id: yup.string(),
  name: yup.string().required(),
  category: yup.mixed<EmissionSourceCategory>().oneOf(Object.values(EmissionSourceCategory)).defined(),
  subCategory: yup.mixed<EmissionSourceSubCategory>().oneOf(Object.values(EmissionSourceSubCategory)).defined(),
  documents: yup.array(
    yup.object().shape({
      fileId: stringRequiredSchema,
      type: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).defined(),
    }),
  ),
  assignedUsers: yup.array(),
  // factors will have multiple factors for different requirement, but factorSourceType = DEFAULT is required for every emission and at first.
  // multiple factors can see emission in CAT 4.3
  factors: yup.array(CreateEmissionFactorSchema),
  metadata: CreateEmissionMetadataSchema,
  emissionGroupId: stringSchema,
});

const EmissionSchemaCat1_1_1_2 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    rawMaterial1: yup.string().required().nullable(),
    rawMaterial4_1b: yup.mixed<RawMaterialOrProduct4_1b>().required().nullable(),
    rawMaterial4_1bOtherName: yup.string().required(),
  }),
});

const EmissionSchemaCat1_3 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    industryType: yup.string().required().nullable(),
    industryTypeOtherName: yup.string().required(),
    industryProcess: yup.string().required().nullable(),
    industryProcessOtherName: yup.string().required(),
    rawMaterial2: yup.string().required().nullable(),
    rawMaterial2OtherName: yup.string().required(),
    isForceCustomized: yup.boolean(),
    factorSources: yup.array(yup.string().required()),
  }),
});

const WasteWaterSchema = yup.object().shape({
  treatmentMethod: yup.mixed<TreatmentMethod>().oneOf(Object.values(TreatmentMethod)).required().nullable(),
  dischargeLocation: yup.mixed<DischargeLocation>().oneOf(Object.values(DischargeLocation)).required().nullable(),
  dischargePathwayOrSystemType: yup.string().required().nullable(),
  wastewaterTreatmentRate: numberRequiredSchema.nullable(),
  maxMethaneProduction: numberRequiredSchema.nullable(),
  methaneCorrectionFactor: numberRequiredSchema.nullable(),
  organicMatterProductionPPPD: numberRequiredSchema.nullable(),
  ch4IndustrialWastewaterDischargeFactor: numberRequiredSchema.nullable(),
  ch4EmissionFactor: numberRequiredSchema.nullable(),
  proteinSupplyPPPD: numberRequiredSchema.nullable(),
  nitrogenToProteinRatio: numberRequiredSchema.nullable(),
  nonProteinSaturatedWastewaterFactor: numberRequiredSchema.nullable(),
  n2oIndustrialWastewaterDischargeFactor: numberRequiredSchema.nullable(),
  n2oEmissionFactor: numberRequiredSchema.nullable(),
});

const EmissionSchemaCat1_4 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    fugitiveCategory: yup.mixed<FugitiveCategory>().oneOf(Object.values(FugitiveCategory)).required().nullable(),
    fugitiveCategoryOtherName: stringRequiredSchema,
    fugitiveType: yup.mixed<FugitiveType>().oneOf(Object.values(FugitiveType)).required().nullable(),
    fugitiveTypeOtherName: stringRequiredSchema,
    equipmentType: yup.mixed<FugitiveEquipmentType>().oneOf(Object.values(FugitiveEquipmentType)).required().nullable(),
    equipmentTypeOtherName: stringRequiredSchema,

    // refrigerant
    fugitiveCalculationMethod: yup.mixed<HypotheticalSituationType>().oneOf(Object.values(HypotheticalSituationType)),

    refrigerantComposition: yup.array().of(
      yup.object().shape({
        refrigerant: stringRequiredSchema,
        chemicalFormulaName: stringRequiredSchema,
        shortName: stringRequiredSchema,
        isMontrealRegulated: yup.boolean().required(),
        gwp: numberSchema, // only record when refrigerant = 'CUSTOM
        gasComposition: yup.array().of(
          yup.object({
            chemicalFormulaName: yup.string(),
            shortName: yup.string(),
            ratio: yup.number(),
            isIncluded: yup.boolean(),
            isMontrealRegulated: yup.boolean(),
          }),
        ),
      }),
    ),
    fugitiveRateCalculation: yup
      .object({
        emissionFactor: yup.number(),
        recoveryEfficiency: yup.number(),
        preventionEquipmentUsageRate: yup.number(),
      })
      .optional(),
    sewagePool: yup.object().shape({
      bodFactor: numberSchema,
      averageWastewaterConcentration: numberSchema,
      perCapitaHourlyWastewaterVolume: numberSchema,
      treatmentEfficiency: numberSchema,
    }),

    customWastewater: WasteWaterSchema,

    ghgsSetting: yup
      .object()
      .shape({
        ghgs: yup.array(yup.mixed<EmissionSourceGreenHouseGases>().oneOf(Object.values(EmissionSourceGreenHouseGases))),
        multi: yup.boolean(),
        disabled: yup.boolean(),
      })
      .nullable(),
  }),
});

const EmissionSchemaCat2 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    purchasedOrAcquiredElectricityCategory: yup
      .mixed<PurchasedElectricityType>()
      .oneOf(Object.values(PurchasedElectricityType))
      .nullable(),
    purchasedOrAcquiredElectricityCategoryKey: stringSchema,
    purchasedEnergyTypeKey: stringSchema,
    marketBased: yup.boolean(),
    locationBased: yup.boolean(),
  }),
});

const EmissionSchemaCat3_1 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    rawMaterialAcquisitionId: stringRequiredSchema,
    activityUnitType: yup
      .mixed<EmissionSourceFactorUnitType>()
      .oneOf(Object.values(EmissionSourceFactorUnitType))
      .defined(),
    activityUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    transportationRoute: yup.mixed<RMTRouteType>().oneOf(Object.values(RMTRouteType)).defined(),
    grossWeight: numberSchema,
    grossWeightUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
  }),
});

const EmissionSchemaCat3_2 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    transportationId: stringSchema,
    transportationVersion: stringSchema,
    distributionId: stringSchema,
    customerCode: stringSchema,
    customerName: stringSchema,
    receivingLocationId: stringSchema,
    receivingLocationCode: stringSchema,
    receivingLocationName: stringSchema,
    version: stringSchema,
    productMaterialActivatedAt: stringSchema,
    productMaterialGrossWeight: numberSchema,
    productMaterialNetWeight: numberSchema,
    productMaterialUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    productSeriesId: stringSchema,
    country: stringSchema.defined(),
    area: stringSchema.defined(),
    areaOther: stringSchema,
    partNo: stringSchema,
    productSeriesName: stringSchema,
    productLineName: stringSchema,
  }),
});

const EmissionSchemaCat3_3 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    employeeIdentifier: stringSchema,
    employeeName: stringSchema,
    employeeDivisionDepartment: stringSchema,
    commutingRouteName: stringSchema,
    departure: yup.object({ address: stringRequiredSchema }).nullable(),
    destination: yup.object({ address: stringRequiredSchema }).nullable(),
    version: stringSchema,
    transportationId: stringSchema,
    transportationRoute: yup.mixed<RMTRouteType>().oneOf(Object.values(RMTRouteType)).nullable(),
    transportationRouteTripType: yup.mixed<RouteTripType>().oneOf(Object.values(RouteTripType)).nullable(),
    segments: yup.array().of(
      yup.object({
        id: stringRequiredSchema,
        seq: numberRequiredSchema,
        factor: numberRequiredSchema,
        factorType: yup.mixed<EmissionSourceFactorType>().oneOf(Object.values(EmissionSourceFactorType)),
        factorSource: stringRequiredSchema,
        factorSourceKey: stringRequiredSchema,
        factorName: stringRequiredSchema,
        factorNameKey: stringRequiredSchema,
        factorBeginAt: stringRequiredSchema,
        factorErrorType: yup
          .mixed<EmissionFactorByFactorErrorType>()
          .oneOf(Object.values(EmissionFactorByFactorErrorType)),
        ghgUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
        distance: stringRequiredSchema,
        means: yup.mixed<RMTMeans>().oneOf(Object.values(RMTMeans)),
        mode: yup.mixed<RMTMode>().oneOf(Object.values(RMTMode)),
        distanceUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
        activityUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)),
        activityUnitType: yup.mixed<EmissionSourceFactorUnitType>().oneOf(Object.values(EmissionSourceFactorUnitType)),
        endLocation: yup.mixed<EmployeeCommutingLocationDetail>(),
        startLocation: yup.mixed<EmployeeCommutingLocationDetail>(),
        documents: yup.array(
          yup.object().shape({
            id: stringRequiredSchema,
            type: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)).defined(),
          }),
        ),
      }),
    ),
  }),
});

const EmissionSchemaCat5_1_5_3 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    transportationId: stringSchema,
    distributionId: stringSchema,
    customerCode: stringSchema,
    customerName: stringSchema,
    receivingLocationId: stringSchema,
    receivingLocationCode: stringSchema,
    receivingLocationName: stringSchema,
    scenarioCountryAreaId: stringSchema,
    productSeriesId: stringSchema,
    country: stringSchema.defined(),
    area: stringSchema.defined(),
    areaOther: stringSchema,
    scenarioStatus: yup.mixed<ScenarioStatus>().oneOf(Object.values(ScenarioStatus)),
    version: stringSchema,
    partNo: stringSchema,
    productSeriesName: stringSchema,
    productLineName: stringSchema,
  }),
});

const EmissionSchemaCat4_1A = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    rawMaterialAcquisitionId: stringRequiredSchema,
    vendorCode: stringRequiredSchema,
    vendorPartNo: stringRequiredSchema,
    shipmentLocationCode: stringRequiredSchema,
    source: stringRequiredSchema,
    netWeight: numberSchema,
    grossWeight: numberSchema,
    materialUnitType: yup
      .mixed<EmissionSourceFactorUnitType>()
      .oneOf(Object.values(EmissionSourceFactorUnitType))
      .defined(),
    materialUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    activityUnitType: yup
      .mixed<EmissionSourceFactorUnitType>()
      .oneOf(Object.values(EmissionSourceFactorUnitType))
      .defined(),
    activityUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    primaryCalculatedData: numberSchema,
    primaryMaterialUnitType: yup
      .mixed<EmissionSourceFactorUnitType>()
      .oneOf(Object.values(EmissionSourceFactorUnitType))
      .defined(),
    primaryMaterialUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    primaryActivityUnitType: yup
      .mixed<EmissionSourceFactorUnitType>()
      .oneOf(Object.values(EmissionSourceFactorUnitType))
      .defined(),
    primaryActivityUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
    ghgMaterialUnit: yup.mixed<ActivityUnit>().oneOf(Object.values(ActivityUnit)).defined(),
  }),
});

const EmissionSchemaCat4_1B = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    relatedFuelEnergyCategory: yup
      .mixed<CatOfActivityRelatedToFuelEnergy>()
      .oneOf(Object.values(CatOfActivityRelatedToFuelEnergy))
      .defined(),
    rawMaterial2: yup.string().required().nullable(),
    rawMaterial2OtherName: yup.string().required(),
    unitConversionFactor: CreateEmissionUnitConversionSchema.nullable(),
  }),
});

const EmissionSchemaCat4_3 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    emissionType: yup
      .mixed<WasteOperationEmissionType>()
      .oneOf(Object.values(WasteOperationEmissionType))
      .required()
      .nullable(),
    emissionTypeOther: yup.string(),
    emissionType1: yup.string().oneOf(Object.values(EmissionTypeOneOptions)).nullable(),
    emissionTypeOther1: yup.string(),
    emissionType2: yup.string().oneOf(Object.values(EmissionTypeTwoOptions)).nullable(),
    emissionTypeOther2: yup.string(),
    rawMaterialNumber: stringSchema,
    wasteManagementCode: yup.string().required(),
    wasteSiteCode: yup.string().required(),
  }),
});

const EmissionSchemaCat4_5 = CreateEmissionBaseSchema.shape({
  metadata: CreateEmissionMetadataSchema.shape({
    emissionType: yup.mixed<ServiceEmissionType>().oneOf(Object.values(ServiceEmissionType)).required().nullable(),
    emissionTypeOther: yup.string(),
  }),
});

export type WasteWaterSchemaType = yup.InferType<typeof WasteWaterSchema>;

export type EmissionSchemaTypeCat1_1_1_2 = yup.InferType<typeof EmissionSchemaCat1_1_1_2>;
export type EmissionSchemaTypeCat1_3 = yup.InferType<typeof EmissionSchemaCat1_3>;
export type EmissionSchemaTypeCat1_4 = yup.InferType<typeof EmissionSchemaCat1_4>;
export type EmissionSchemaTypeCat2 = yup.InferType<typeof EmissionSchemaCat2>;
export type EmissionSchemaTypeCat3_1 = yup.InferType<typeof EmissionSchemaCat3_1>;
export type EmissionSchemaTypeCat3_2 = yup.InferType<typeof EmissionSchemaCat3_2>;
export type EmissionSchemaTypeCat3_3 = yup.InferType<typeof EmissionSchemaCat3_3>;
export type EmissionSchemaTypeCat4_1A = yup.InferType<typeof EmissionSchemaCat4_1A>;
export type EmissionSchemaTypeCat4_1B = yup.InferType<typeof EmissionSchemaCat4_1B>;
export type EmissionSchemaTypeCat4_3 = yup.InferType<typeof EmissionSchemaCat4_3>;
export type EmissionSchemaTypeCat4_5 = yup.InferType<typeof EmissionSchemaCat4_5>;
export type EmissionSchemaTypeCat5_1_5_3 = yup.InferType<typeof EmissionSchemaCat5_1_5_3>;

export type CreateEmissionType = EmissionSchemaTypeCat1_1_1_2 &
  EmissionSchemaTypeCat1_3 &
  EmissionSchemaTypeCat1_4 &
  EmissionSchemaTypeCat2 &
  EmissionSchemaTypeCat3_1 &
  EmissionSchemaTypeCat3_2 &
  EmissionSchemaTypeCat3_3 &
  EmissionSchemaTypeCat4_1A &
  EmissionSchemaTypeCat4_1B &
  EmissionSchemaTypeCat4_3 &
  EmissionSchemaTypeCat4_5 &
  EmissionSchemaTypeCat5_1_5_3;

export type CreateEmissionTypeFieldPath = FieldPath<CreateEmissionType>;
export type WasteWaterSchemaTypeFieldPath = FieldPath<WasteWaterSchemaType>;
export type GasData = NonNullable<NonNullable<CreateEmissionType['factors']>[0]['gasData']>;

const BASE_FIELD_CAN_NOT_EDIT: CreateEmissionTypeFieldPath[] = [
  'metadata.greenHouseEmissionType',
  'metadata.measurementMethod',
];

export const EMISSION_CAN_NOT_EDIT_FIELD_SETTING: Partial<
  Record<EmissionSourceSubCategory, CreateEmissionTypeFieldPath[]>
> = {
  [EmissionSourceSubCategory.CAT_1_1]: [...BASE_FIELD_CAN_NOT_EDIT],
  [EmissionSourceSubCategory.CAT_1_2]: [...BASE_FIELD_CAN_NOT_EDIT],
  [EmissionSourceSubCategory.CAT_1_3]: [
    ...BASE_FIELD_CAN_NOT_EDIT,
    'metadata.industryType',
    'metadata.industryProcess',
    'metadata.reportRawMaterial1',
    'metadata.rawMaterial2',
  ],
  [EmissionSourceSubCategory.CAT_1_4]: [
    ...BASE_FIELD_CAN_NOT_EDIT,
    'metadata.fugitiveCategory',
    'metadata.fugitiveType',
    'metadata.equipmentType',
    //
    'metadata.customWastewater.treatmentMethod',
  ],
  [EmissionSourceSubCategory.CAT_2_1]: [...BASE_FIELD_CAN_NOT_EDIT, 'metadata.purchasedOrAcquiredElectricityCategory'],
  [EmissionSourceSubCategory.CAT_2_2]: [...BASE_FIELD_CAN_NOT_EDIT, 'factors.0.type'],
  [EmissionSourceSubCategory.CAT_4_1B]: [
    ...BASE_FIELD_CAN_NOT_EDIT,
    'metadata.relatedFuelEnergyCategory',
    'metadata.rawMaterial4_1b',
  ],
  [EmissionSourceSubCategory.CAT_4_3]: [
    ...BASE_FIELD_CAN_NOT_EDIT,
    'metadata.wasteManagementCode',
    'metadata.wasteSiteCode',
  ],
  [EmissionSourceSubCategory.CAT_4_5]: [...BASE_FIELD_CAN_NOT_EDIT],
};

export const EMISSION_CAN_REUPLOAD_SETTING = {
  [DocumentType.GENERAL]: true,
  [DocumentType.FACTOR]: true,
  [DocumentType.NAME_PLATE]: true,
  [DocumentType.REFRIGERANT]: true,
  [DocumentType.SPECIFICATIONS]: true,
  [DocumentType.PCR]: true,
  [DocumentType.WEEE]: true,
  [DocumentType.TD_LOSS]: true,
  [DocumentType.UNIT_CONVERSION]: true,
  [DocumentType.AI]: true,
  [DocumentType.USER]: true,
};
